import { ref } from 'vue';
import { getOIDCConfig } from '@/api/auth';

/**
 * OIDC/Keycloak Integration Composable
 * Handles OAuth2 Authorization Code Flow with PKCE
 */
export function useOIDC() {
  const oidcConfig = ref(null);
  const oidcError = ref(null);

  /**
   * Load OIDC configuration from backend
   */
  async function loadOIDCConfig() {
    try {
      const response = await getOIDCConfig();
      oidcConfig.value = response.data;
      return oidcConfig.value;
    } catch (error) {
      console.error('Failed to load OIDC config:', error);
      oidcError.value = error.response?.data?.detail || 'OIDC not available';
      return null;
    }
  }

  /**
   * Generate random string for PKCE
   */
  function generateRandomString(length = 43) {
    if (!window.crypto?.getRandomValues) {
      throw new Error('Secure browser cryptography is unavailable. Open the application over HTTPS.');
    }

    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => ('0' + byte.toString(16)).slice(-2)).join('');
  }

  /**
   * Generate SHA-256 hash and base64url encode
   */
  async function sha256(plain) {
    if (!window.crypto?.subtle) {
      throw new Error('PKCE requires Web Crypto. Open the application over HTTPS in a supported browser.');
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    return base64urlEncode(hash);
  }

  /**
   * Base64url encode (without padding)
   */
  function base64urlEncode(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Generate PKCE code_verifier and code_challenge
   */
  async function generatePKCE() {
    const codeVerifier = generateRandomString(43);
    const codeChallenge = await sha256(codeVerifier);
    return { codeVerifier, codeChallenge };
  }

  /**
   * Initiate OIDC login flow
   * Redirects to Keycloak authorization endpoint
   */
  async function initiateOIDCLogin() {
    if (!oidcConfig.value) {
      await loadOIDCConfig();
    }

    if (!oidcConfig.value || !oidcConfig.value.enabled) {
      throw new Error('OIDC is not enabled');
    }

    // Generate PKCE parameters
    const { codeVerifier, codeChallenge } = await generatePKCE();
    const state = generateRandomString(32);

    // Store PKCE parameters in sessionStorage
    sessionStorage.setItem('oidc_code_verifier', codeVerifier);
    sessionStorage.setItem('oidc_state', state);

    // Build redirect_uri dynamically from current origin
    const redirectUri = `${window.location.origin}/auth/callback`;

    // Build authorization URL
    const params = new URLSearchParams({
      client_id: oidcConfig.value.client_id,
      response_type: 'code',
      scope: 'openid profile email',
      redirect_uri: redirectUri,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const authUrl = `${oidcConfig.value.authorization_endpoint}?${params.toString()}`;

    // Redirect to Keycloak
    window.location.href = authUrl;
  }

  /**
   * Initiate OIDC logout flow
   * Redirects to Keycloak logout endpoint to end the SSO session
   */
  async function initiateOIDCLogout() {
    if (!oidcConfig.value) {
      await loadOIDCConfig();
    }

    if (!oidcConfig.value || !oidcConfig.value.enabled || !oidcConfig.value.logout_endpoint) {
      console.warn('OIDC logout endpoint not available');
      return;
    }

    // Build post_logout_redirect_uri (where to redirect after logout)
    const postLogoutRedirectUri = `${window.location.origin}/login`;

    // Build logout URL with redirect
    const params = new URLSearchParams({
      client_id: oidcConfig.value.client_id,
      post_logout_redirect_uri: postLogoutRedirectUri,
    });

    const logoutUrl = `${oidcConfig.value.logout_endpoint}?${params.toString()}`;

    // Redirect to Keycloak logout
    window.location.href = logoutUrl;
  }

  return {
    oidcConfig,
    oidcError,
    loadOIDCConfig,
    initiateOIDCLogin,
    initiateOIDCLogout,
  };
}
